import React from "react";
import { api } from "@/lib/admin/api";
import { PageHead } from "@/pages/admin/Layout";
import { TextInput, toast } from "@/components/admin/ui";

/* ====================================================================
   2High · Owner Cabinet — Inquiries Inbox
   --------------------------------------------------------------------
   Left list of inquiries · right slide-out detail panel.
   Statuses: new · confirmed · shipped · closed
   ==================================================================== */

const STATUSES = [
  { id: "new",       label: "New",       cls: "bg-[#5fe28a]/15 text-[#5fe28a] border-[#5fe28a]/40" },
  { id: "confirmed", label: "Confirmed", cls: "bg-[#c9a86b]/15 text-[#c9a86b] border-[#c9a86b]/40" },
  { id: "shipped",   label: "Shipped",   cls: "bg-[#9aa0c4]/15 text-[#9aa0c4] border-[#9aa0c4]/40" },
  { id: "closed",    label: "Closed",    cls: "bg-white/5 text-[#a9aa9a] border-[#2c3122]" },
];
const STATUS_META = Object.fromEntries(STATUSES.map((s) => [s.id, s]));

export default function Inquiries() {
  const [items, setItems] = React.useState([]);
  const [filter, setFilter] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [selectedId, setSelectedId] = React.useState(null);
  const [reply, setReply] = React.useState("");

  const reload = React.useCallback(async () => {
    try {
      const list = await api.listInquiries(filter || undefined);
      setItems(list);
    } catch (e) { toast(e.response?.data?.detail || "Failed to load inquiries", "error"); }
  }, [filter]);
  React.useEffect(() => { reload(); }, [reload]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (!q) return true;
      return [it.name, it.note, it.notes, it.path, it.telegram, it.region, it.contact_method, it.interested_product, it.city]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [items, search]);

  const selected = React.useMemo(() => items.find((it) => it.id === selectedId) || null, [items, selectedId]);

  async function setStatus(id, status) {
    try {
      await api.updateInquiry(id, { status });
      toast(`Marked ${status}`);
      await reload();
    } catch (e) { toast("Update failed", "error"); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await api.deleteInquiry(id);
      toast("Deleted");
      if (selectedId === id) setSelectedId(null);
      await reload();
    } catch (e) { toast("Delete failed", "error"); }
  }

  const counts = React.useMemo(() => {
    const c = { all: items.length };
    STATUSES.forEach((s) => { c[s.id] = items.filter((i) => i.status === s.id).length; });
    return c;
  }, [items]);

  return (
    <div data-testid="inquiries-page" className="-mx-4 lg:-mx-8 -my-4 lg:-my-8 min-h-screen px-4 lg:px-8 py-4 lg:py-8">
      <PageHead
        eye={`Inbox · ${items.length} inquir${items.length === 1 ? "y" : "ies"} · ${counts.new || 0} new`}
        title="Inquiries"
        sub="Form submissions from the public site. Most CTAs also ping your Telegram directly for immediate handoff."
      />

      {/* Toolbar */}
      <div className="mb-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 p-2.5 rounded-xl bg-[#13150f] border border-[#22261b]">
        <div className="relative">
          <svg className="absolute left-3 top-3 opacity-60" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 21l-4.3-4.3M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"/></svg>
          <input
            data-testid="inq-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, product, region, note…"
            className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-[#0e110a] border border-[#22261b] text-[13px] focus:outline-none focus:border-[#c9a86b]"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <FilterChip data-testid="inq-filter-all" active={filter === ""} onClick={() => setFilter("")} label={`All · ${counts.all}`} />
          {STATUSES.map((s) => (
            <FilterChip
              key={s.id}
              data-testid={`inq-filter-${s.id}`}
              active={filter === s.id}
              onClick={() => setFilter(s.id)}
              label={`${s.label} · ${counts[s.id] || 0}`}
            />
          ))}
        </div>
      </div>

      <div className={`grid gap-5 ${selectedId ? "grid-cols-1 xl:grid-cols-[1fr_640px]" : "grid-cols-1"}`}>
        {/* LIST */}
        <section data-testid="inq-list" className="rounded-2xl border border-[#22261b] bg-[#13150f] overflow-hidden">
          <div className="grid grid-cols-[1.4fr_1fr_0.7fr_120px_120px] gap-3 items-center px-4 py-3 text-[10px] tracking-[.22em] uppercase text-[#6e6f5f] bg-[#10120c] border-b border-[#22261b]">
            <div>From</div>
            <div>Looking for</div>
            <div>Status</div>
            <div>Received</div>
            <div className="text-right">Actions</div>
          </div>
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-[#6e6f5f] text-[13px]">No inquiries match.</div>
          ) : filtered.map((q) => {
            const status = STATUS_META[q.status] || STATUS_META.new;
            const sel = selectedId === q.id;
            return (
              <div
                key={q.id}
                data-testid={`inq-row-${q.id}`}
                onClick={() => { setSelectedId(q.id); setReply(""); }}
                className={`grid grid-cols-[1.4fr_1fr_0.7fr_120px_120px] gap-3 items-center px-4 py-3 border-b border-[#22261b] cursor-pointer transition ${
                  sel ? "bg-[#181c12] shadow-[inset_3px_0_0_#c9a86b]" : "hover:bg-[#10130c]"
                }`}
              >
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold truncate">{q.name || "(no name)"}</div>
                  <div className="text-[11px] text-[#a9aa9a] truncate">
                    {[q.contact_method || q.telegram, q.region || q.city].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <div className="min-w-0 text-[12.5px] text-[#a9aa9a] truncate">
                  {q.interested_product || q.path || "—"}
                </div>
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-mono tracking-[.04em] border ${status.cls}`}>
                    {status.label}
                  </span>
                </div>
                <div className="text-[10.5px] font-mono text-[#6e6f5f]">
                  {timeAgo(q.received_at || q.created_at || q.date)}
                </div>
                <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                  {q.status !== "new" && (
                    <button data-testid={`inq-mark-new-${q.id}`} onClick={() => setStatus(q.id, "new")} title="Mark new" className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-[#5fe28a] hover:text-[#5fe28a]">●</button>
                  )}
                  <button data-testid={`inq-delete-${q.id}`} onClick={() => remove(q.id)} title="Delete" className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-red-400 hover:text-red-400">×</button>
                </div>
              </div>
            );
          })}
        </section>

        {/* DETAIL PANEL */}
        {selected && (
          <Detail
            key={selected.id}
            inq={selected}
            reply={reply}
            setReply={setReply}
            onClose={() => setSelectedId(null)}
            onSetStatus={(s) => setStatus(selected.id, s)}
            onDelete={() => remove(selected.id)}
          />
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, ...rest }) {
  return (
    <button {...rest} onClick={onClick} className={`px-3 py-2 rounded-lg text-[10.5px] tracking-[.18em] uppercase border transition ${
      active ? "bg-[#c9a86b] text-[#1a1407] border-[#c9a86b]" : "bg-[#0e110a] text-[#e8e4d8]/70 border-[#22261b] hover:border-[#c9a86b]"
    }`}>{label}</button>
  );
}

function Detail({ inq, reply, setReply, onClose, onSetStatus, onDelete }) {
  const status = STATUS_META[inq.status] || STATUS_META.new;
  const tgHandle = (inq.contact_method || inq.telegram || "").replace(/^@/, "");
  const tgLink = tgHandle ? `https://t.me/${encodeURIComponent(tgHandle)}` : "";

  function copy(text) {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => toast("Copied"));
  }

  return (
    <aside data-testid="inq-detail-panel" className="rounded-2xl border border-[#22261b] bg-[#13150f] overflow-hidden flex flex-col xl:sticky xl:top-6 xl:max-h-[calc(100vh-3rem)]">
      <div className="flex items-start justify-between px-5 py-4 border-b border-[#22261b] bg-[#10120c]">
        <div>
          <h2 className="text-[16px] font-bold leading-tight">{inq.name || "(no name)"}</h2>
          <div className="text-[10.5px] tracking-[.08em] font-mono text-[#6e6f5f] mt-1">
            id · {(inq.id || "").slice(0, 8)} · {timeAgo(inq.received_at || inq.created_at || inq.date)}
          </div>
        </div>
        <button data-testid="inq-close" onClick={onClose} className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b]">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </div>

      <div className="px-5 py-5 overflow-y-auto flex-1 space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className={`inline-flex items-center px-2 py-1 rounded-md text-[10.5px] font-mono tracking-[.04em] border ${status.cls}`}>{status.label}</span>
            <span className="text-[10.5px] font-mono text-[#6e6f5f]">{new Date(inq.received_at || inq.created_at || inq.date || Date.now()).toLocaleString()}</span>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {STATUSES.map((s) => (
              <button
                key={s.id}
                data-testid={`inq-set-${s.id}`}
                onClick={() => onSetStatus(s.id)}
                disabled={inq.status === s.id}
                className={`px-3 py-2 rounded-md text-[10.5px] tracking-[.18em] uppercase border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                  inq.status === s.id ? `${s.cls}` : "bg-[#0e110a] border-[#22261b] text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b]"
                }`}
              >→ Mark {s.label}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 pb-5 border-b border-[#22261b]">
          <Detail2 label="Contact method" value={inq.contact_method || inq.telegram || "—"} onCopy={() => copy(inq.contact_method || inq.telegram)} />
          <Detail2 label="Region / city" value={inq.region || inq.city || "—"} />
          <Detail2 label="Looking for" value={inq.interested_product || inq.path || "—"} onCopy={() => copy(inq.interested_product || inq.path)} />
          <Detail2 label="Quantity" value={inq.quantity || "—"} />
        </div>

        {(inq.note || inq.notes) && (
          <div>
            <div className="text-[10.5px] tracking-[.16em] uppercase text-[#6e6f5f] mb-2">Note from member</div>
            <div className="rounded-lg bg-[#0e110a] border border-[#22261b] px-3.5 py-3 text-[13px] text-[#e8e4d8] italic leading-relaxed">
              "{inq.note || inq.notes}"
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] tracking-[.16em] uppercase text-[#6e6f5f]">Quick reply scratchpad</div>
            <button onClick={() => copy(reply)} className="text-[10.5px] tracking-[.18em] uppercase text-[#c9a86b] hover:text-white">Copy</button>
          </div>
          <textarea
            data-testid="inq-reply"
            value={reply} onChange={(e) => setReply(e.target.value)}
            rows={5}
            placeholder="Draft a Telegram reply here, then copy it over to your DM…"
            className="w-full px-3 py-2.5 rounded-lg bg-[#0e110a] border border-[#22261b] text-[13px] focus:outline-none focus:border-[#c9a86b]"
          />
          <div className="mt-1.5 text-[10.5px] text-[#6e6f5f]">Saved nowhere — purely a scratchpad.</div>
        </div>
      </div>

      <div className="px-5 py-4 border-t border-[#22261b] bg-[#10120c] flex items-center justify-between gap-3 flex-wrap">
        <button data-testid="inq-delete-btn" onClick={onDelete} className="px-3 py-2 rounded-md border border-red-500/40 text-red-300 hover:bg-red-500/10 text-[10.5px] tracking-[.2em] uppercase">Delete</button>
        <div className="flex gap-2">
          {tgLink && (
            <a data-testid="inq-open-telegram" href={tgLink} target="_blank" rel="noreferrer" className="px-4 py-2.5 rounded-md text-[12px] tracking-[.2em] uppercase font-bold" style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}>
              Open in Telegram →
            </a>
          )}
        </div>
      </div>
    </aside>
  );
}

function Detail2({ label, value, onCopy }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-[.14em] uppercase text-[#6e6f5f] mb-1">{label}</div>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[13px] text-[#e8e4d8] break-words flex-1">{value}</div>
        {onCopy && value && value !== "—" && (
          <button onClick={onCopy} title="Copy" className="text-[10px] tracking-[.18em] uppercase text-[#6e6f5f] hover:text-[#c9a86b]">copy</button>
        )}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
